import { defineStore } from 'pinia'
import { useApiRequest } from '@/api'
import { type BrandKit, useBrandLogosResource } from '../resources'
import { useBrandKitStore } from './brand-kit.store'

type LogosResource = ReturnType<typeof useBrandLogosResource>
type LogoBody = Parameters<LogosResource['setBrandLogo']>[3]

export const useBrandLogosStore = defineStore('brand-logos', () => {
  const { setBrandLogo, setBrandLogoFile, delBrandLogo } = useBrandLogosResource()
  const kitStore = useBrandKitStore()
  const { run, loading, failure } = useApiRequest()

  const placeInKit = async (brandId: string, call: Parameters<typeof run>[0]) => {
    const logo = await run<BrandKit['logos'][number]>(call)
    const kit = kitStore.kits[brandId]
    if (logo && kit) {
      const rest = kit.logos.filter(row => row.slot !== logo.slot)
      const logos = [...rest, logo].sort((left, right) => left.slot.localeCompare(right.slot))
      kitStore.kits = { ...kitStore.kits, [brandId]: { ...kit, logos } }
    }
    return logo
  }

  const saveKitLogo = (organizationId: string, brandId: string, slot: string, body: LogoBody) =>
    placeInKit(brandId, () => setBrandLogo(organizationId, brandId, slot, body))

  const uploadKitLogo = (organizationId: string, brandId: string, slot: string, file: File) =>
    placeInKit(brandId, () => setBrandLogoFile(organizationId, brandId, slot, file))

  const removeKitLogo = async (organizationId: string, brandId: string, slot: string) => {
    await run(() => delBrandLogo(organizationId, brandId, slot))
    if (failure.value) return false
    const kit = kitStore.kits[brandId]
    if (kit) kitStore.kits = { ...kitStore.kits, [brandId]: { ...kit, logos: kit.logos.filter(logo => logo.slot !== slot) } }
    return true
  }

  return { loading, failure, saveKitLogo, uploadKitLogo, removeKitLogo }
})
